import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import ConfirmModal from "../../components/common/confirmModal";
import { ImageCropModal } from "../../components/common/imageCropModal";
import ProfileSettingsTab from "../../pages/settings/profileSettingsTab";
import {
    cancelAccountDeletion,
    fetchCountries,
    fetchTrainingDisciplines_signup,
    fetchWeightClasses_signup,
    requestAccountDeletion,
    updateUser,
    uploadMediaFile,
} from "../../services/api";
import { Profile, UpdateManagerDto, UpdateUserDto } from "../../types/types";
import {
    FightItem,
    FormErrors,
    MEASURE_RANGES,
    MeasureKey,
    ProfileForm,
    ProfileTopBarActions,
    SectionKey,
    Snapshot,
    SocialLink,
    SponsorItem,
    UnitKey,
    UpdateProfileUIProps,
    YoutubeItem,
} from "../../types/updateProfileProps";

type Props = {
    member: Profile;
    onClose: () => void;
    onSubmitSuccess: () => void | Promise<void>;
    /** Lets the page put Cancel / Save changes into its top bar (null = nothing to show) */
    onActionsChange?: (actions: ProfileTopBarActions | null) => void;
};

// ─── Constants ──────────────────────────────────────────────────────────────
const MAX_AVATAR_BYTES = 10 * 1024 * 1024;
const GENDERS = ["MALE", "FEMALE", "OTHER"];

const SECTION_KEYS: SectionKey[] = ["bio", "personal", "location", "fighterBasic", "fighterAdditional"];

/** Which `ProfileForm` fields belong to which Figma "Save change" button */
const SECTION_FIELDS: Record<SectionKey, (keyof ProfileForm)[]> = {
    bio: ["bio"],
    personal: ["firstName", "lastName", "gender", "nickname", "dateOfBirth", "organizationName"],
    location: ["nationality", "residenceCountry"],
    fighterBasic: [
        "trainingDisciplines",
        "weightClasses",
        "height",
        "heightUnit",
        "weight",
        "weightUnit",
        "reach",
        "reachUnit",
        "stance",
        "titleHolder",
    ],
    fighterAdditional: ["team", "gym", "fightStyleDescription"],
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const isSame = (first: unknown, second: unknown) => {
    return JSON.stringify(first) === JSON.stringify(second);
};

const isEmpty = (value: string | string[] | boolean) => {
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "string") return value.trim() === "";
    return false;
};

/** Copies one field between two form objects while keeping the types in sync */
const copyField = <K extends keyof ProfileForm>(target: Partial<ProfileForm>, source: ProfileForm, key: K) => {
    target[key] = source[key];
};

const getErrorMessage = (err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
        const body = err.response?.data;
        return body?.message || body?.errors?.[0] || fallback;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
};

/** The delete-account endpoints are not in the backend yet (BACKEND_CHANGES.md #5) */
const isMissingEndpoint = (err: unknown): boolean => {
    if (!axios.isAxiosError(err)) return false;
    const status = err.response?.status;
    return status === 404 || status === 405;
};

const normalizeGender = (gender?: string | null) => {
    const value = (gender ?? "").trim().toUpperCase();
    return GENDERS.includes(value) ? value : "";
};

/** "2000-05-01T00:00:00" -> "2000-05-01" */
const getDatePart = (value?: string | null) => (value ?? "").split("T")[0];

/** Today as yyyy-MM-dd in the user's LOCAL time zone (toISOString would use UTC) */
const getTodayIso = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
};

export const toSnapshot = (member: Profile): Snapshot => {
    const fighter = member.fighter;
    const isFighter = member.profileType === "FIGHTER";
    const links = (isFighter ? fighter?.socialMediaLinks : member.manager?.socialMediaLinks) ?? [];

    return {
        bio: member.bio ?? "",
        firstName: member.firstName ?? "",
        lastName: member.lastName ?? "",
        gender: normalizeGender(member.gender),
        nickname: fighter?.nickname ?? "",
        dateOfBirth: getDatePart(fighter?.dateOfBirth),
        organizationName: member.manager?.organizationName ?? "",
        nationality: member.nationality ?? "",
        residenceCountry: member.residenceCountry ?? "",
        trainingDisciplines: fighter?.trainingDisciplines ?? [],
        weightClasses: fighter?.weightClasses ?? [],
        height: fighter?.height ? String(fighter.height) : "",
        heightUnit: fighter?.heightUnit || "cm",
        weight: fighter?.weight ? String(fighter.weight) : "",
        weightUnit: fighter?.weightUnit || "kgs",
        reach: fighter?.reach ? String(fighter.reach) : "",
        reachUnit: fighter?.reachUnit || "cm",
        stance: fighter?.stance ?? "",
        titleHolder: !!(fighter?.titleHolder ?? fighter?.isTitleHolder),
        team: fighter?.team ?? "",
        gym: fighter?.gym ?? "",
        fightStyleDescription: fighter?.fightStyleDescription ?? "",
        socialLinks: links.map((link) => ({
            type: String(link.socialMediaAccountType ?? "").toUpperCase(),
            url: link.socialMediaAccountUrl ?? "",
        })),
        achievements: (fighter?.achievementDetails ?? []).map((achievement) => achievement.text),
        fights: (fighter?.fightHistories ?? []).map((fight) => ({
            opponentName: fight.opponentName ?? "",
            date: getDatePart(fight.date),
            location: fight.location ?? "",
            result: fight.result ?? "",
            round: fight.round ?? 1,
            typeOfEnding: String(fight.typeOfEnding ?? "").toUpperCase(),
            organizationName: fight.organizationName ?? undefined,
            preferredRuleSet: String(fight.preferredRuleSet ?? "").toUpperCase(),
        })),
        ytLinks: (fighter?.ytLinksDetails ?? []).map((item) => ({
            ytLink: item.ytLink ?? "",
            shortTitle: item.shortTitle ?? "",
            shortDescription: item.shortDescription ?? "",
        })),
        sponsors: (fighter?.sponsorsResponseDtos ?? []).map((sponsor) => ({
            id: sponsor.id,
            name: sponsor.name ?? "",
            description: sponsor.description ?? "",
            logo: sponsor.logo ?? "",
            link: sponsor.link ?? "",
            createdAt: sponsor.createdAt ?? null,
        })),
    };
};

const toForm = (snapshot: Snapshot): ProfileForm => ({
    bio: snapshot.bio,
    firstName: snapshot.firstName,
    lastName: snapshot.lastName,
    gender: snapshot.gender,
    nickname: snapshot.nickname,
    dateOfBirth: snapshot.dateOfBirth,
    organizationName: snapshot.organizationName,
    nationality: snapshot.nationality,
    residenceCountry: snapshot.residenceCountry,
    trainingDisciplines: snapshot.trainingDisciplines,
    weightClasses: snapshot.weightClasses,
    height: snapshot.height,
    heightUnit: snapshot.heightUnit,
    weight: snapshot.weight,
    weightUnit: snapshot.weightUnit,
    reach: snapshot.reach,
    reachUnit: snapshot.reachUnit,
    stance: snapshot.stance,
    titleHolder: snapshot.titleHolder,
    team: snapshot.team,
    gym: snapshot.gym,
    fightStyleDescription: snapshot.fightStyleDescription,
});

/**
 * Builds the FULL PUT /user/profile body from the last saved state + one change.
 * The backend validates the whole FighterDto on every call, and it clears
 * fighter.videoUrl when none is sent – so everything is always sent.
 */
const buildDto = (next: Snapshot, member: Profile, imageUrl?: string): UpdateUserDto => {
    const dto: UpdateUserDto = {
        firstName: next.firstName.trim(),
        lastName: next.lastName.trim(),
        phone: member.phone ?? "",
        nationality: next.nationality,
        residenceCountry: next.residenceCountry,
        bio: next.bio,
        gender: next.gender || undefined,
    };

    if (imageUrl) dto.profileImageUrl = imageUrl;

    if (member.profileType === "FIGHTER") {
        dto.videoUrl = member.fighter?.videoUrl ?? undefined;
        dto.fighterDto = {
            firstName: next.firstName.trim(),
            lastName: next.lastName.trim(),
            nickname: next.nickname.trim() || undefined,
            stance: next.stance,
            team: next.team.trim(),
            gym: next.gym.trim(),
            fightStyleDescription: next.fightStyleDescription.trim(),
            height: parseInt(next.height, 10),
            heightUnit: next.heightUnit,
            weight: parseInt(next.weight, 10),
            weightUnit: next.weightUnit,
            reach: parseInt(next.reach, 10),
            reachUnit: next.reachUnit,
            dateOfBirth: next.dateOfBirth,
            isTitleHolder: next.titleHolder,
            trainingDisciplines: next.trainingDisciplines,
            weightClasses: next.weightClasses,
            achievementDetails: next.achievements.map((text) => ({ text })),
            ytLinkDetailsDto: next.ytLinks,
            fighterSponsorsRequestDtos: next.sponsors.map((sponsor) => ({
                name: sponsor.name,
                description: sponsor.description,
                logo: sponsor.logo,
                link: sponsor.link,
            })),
            socialMediaLinks: next.socialLinks,
            fightHistoryDetailsDto: next.fights.map((fight) => ({
                ...fight,
                organizationName: fight.organizationName?.trim() || undefined,
            })),
        };
    } else {
        const managerDto: UpdateManagerDto = { socialMediaLinks: next.socialLinks };
        if (member.profileType === "MANAGER") managerDto.type = member.manager?.type;
        if (member.profileType === "ORGANIZATION") {
            managerDto.organizationName = next.organizationName.trim();
        }
        dto.managerDto = managerDto;
    }

    return dto;
};

// ─── Component ──────────────────────────────────────────────────────────────
export const UpdateProfileFeature: React.FC<Props> = ({ member, onSubmitSuccess, onActionsChange }) => {
    const { t } = useTranslation("updateProfile");

    const isFighter = member.profileType === "FIGHTER";
    const isManager = member.profileType === "MANAGER";
    const isOrganization = member.profileType === "ORGANIZATION";

    const [saved, setSaved] = useState<Snapshot>(() => toSnapshot(member));
    const [form, setForm] = useState<ProfileForm>(() => toForm(toSnapshot(member)));
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSavingAll, setIsSavingAll] = useState(false);
    const [isPersisting, setIsPersisting] = useState(false);

    // refs always hold the latest value, so async code never reads a stale copy
    const isBusyRef = useRef(false);
    const savedRef = useRef(saved);
    savedRef.current = saved;

    const [countries, setCountries] = useState<string[]>([]);
    const [disciplineOptions, setDisciplineOptions] = useState<string[]>([]);
    const [weightClassOptions, setWeightClassOptions] = useState<string[]>([]);

    const [imageUrl, setImageUrl] = useState<string>(member.profileImage ?? "");
    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    const [deletionAt, setDeletionAt] = useState<string | null>(member.deletionScheduledAt ?? null);
    const [deletionBusy, setDeletionBusy] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // Dropdown data – every list comes from the backend (no hard-coded fallbacks)
    useEffect(() => {
        let isActive = true;

        const loadOptions = async () => {
            try {
                const [countryList, disciplineList, weightClassList] = await Promise.all([
                    fetchCountries(),
                    fetchTrainingDisciplines_signup(),
                    fetchWeightClasses_signup(),
                ]);
                if (!isActive) return;
                setCountries(countryList);
                setDisciplineOptions(disciplineList);
                setWeightClassOptions(weightClassList);
            } catch (err) {
                if (isActive) toast.error(getErrorMessage(err, t("page.loadOptionsFailed")));
            }
        };

        loadOptions();
        return () => {
            isActive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When the parent refreshes `member`, adopt the server copy but keep any
    // section the user is still editing (dirty) untouched.
    useEffect(() => {
        const snapshot = toSnapshot(member);
        const previousSaved = savedRef.current;

        setForm((previousForm) => {
            const nextForm: ProfileForm = { ...previousForm };

            SECTION_KEYS.forEach((section) => {
                const fields = SECTION_FIELDS[section];
                const isUntouched = fields.every((key) => isSame(previousForm[key], previousSaved[key]));
                if (isUntouched) {
                    fields.forEach((key) => copyField(nextForm, snapshot, key));
                }
            });

            return nextForm;
        });

        setSaved(snapshot);
        setImageUrl(member.profileImage ?? "");
        setDeletionAt(member.deletionScheduledAt ?? null);
    }, [member]);

    // ── field helpers ──
    const clearError = (key: string) => {
        setErrors((previous) => {
            if (!previous[key]) return previous;
            const next = { ...previous };
            delete next[key];
            return next;
        });
    };

    const setField: UpdateProfileUIProps["setField"] = (name, value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
        clearError(name);
    };

    const isDirty = (section: SectionKey) => {
        return SECTION_FIELDS[section].some((key) => !isSame(form[key], saved[key]));
    };

    // ── validation ──
    const validateSection = (section: SectionKey): FormErrors => {
        const sectionErrors: FormErrors = {};

        const requireField = (key: keyof ProfileForm, message: string) => {
            if (isEmpty(form[key])) sectionErrors[key] = message;
        };

        const validateMeasure = (key: MeasureKey, unitKey: UnitKey) => {
            requireField(key, t("page.errors.required"));
            if (sectionErrors[key]) return;

            const value = Number(form[key]);
            const unit = form[unitKey];
            const range = MEASURE_RANGES[key][unit];

            if (!Number.isInteger(value)) {
                sectionErrors[key] = t("page.errors.wholeNumber");
            } else if (range && (value < range[0] || value > range[1])) {
                sectionErrors[key] = t(`validation.${key}Range`, {
                    min: range[0],
                    max: range[1],
                    unit,
                });
            }
        };

        if (section === "bio" && form.bio.length > 1000) {
            sectionErrors.bio = t("page.errors.bioLength");
        }

        if (section === "personal") {
            requireField("firstName", t("validation.firstNameRequired"));
            requireField("lastName", t("validation.lastNameRequired"));
            requireField("gender", t("page.errors.genderRequired"));

            if (form.firstName.length > 30) {
                sectionErrors.firstName = t("page.errors.maxLength", { max: 30 });
            }
            if (form.lastName.length > 25) {
                sectionErrors.lastName = t("page.errors.maxLength", { max: 25 });
            }

            if (isFighter) {
                if (form.nickname.length > 25) {
                    sectionErrors.nickname = t("page.errors.maxLength", { max: 25 });
                }
                requireField("dateOfBirth", t("page.errors.dobRequired"));
                if (form.dateOfBirth && form.dateOfBirth >= getTodayIso()) {
                    sectionErrors.dateOfBirth = t("page.errors.dobPast");
                }
            }

            if (isOrganization) {
                requireField("organizationName", t("page.errors.required"));
                if (form.organizationName.length > 40) {
                    sectionErrors.organizationName = t("page.errors.maxLength", { max: 40 });
                }
            }
        }

        if (section === "location") {
            requireField("nationality", t("validation.nationalityRequired"));
            requireField("residenceCountry", t("validation.residenceCountryRequired"));
        }

        if (section === "fighterBasic") {
            requireField("trainingDisciplines", t("page.errors.required"));
            requireField("weightClasses", t("page.errors.required"));
            validateMeasure("height", "heightUnit");
            validateMeasure("weight", "weightUnit");
            validateMeasure("reach", "reachUnit");
            requireField("stance", t("page.errors.required"));
        }

        if (section === "fighterAdditional") {
            if (form.team.length > 30) {
                sectionErrors.team = t("page.errors.maxLength", { max: 30 });
            }
            if (form.gym.length > 30) {
                sectionErrors.gym = t("page.errors.maxLength", { max: 30 });
            }
            requireField("fightStyleDescription", t("page.errors.required"));
            if (form.fightStyleDescription.length > 5000) {
                sectionErrors.fightStyleDescription = t("page.errors.maxLength", { max: 5000 });
            }
        }

        return sectionErrors;
    };

    /** Backend validates the whole payload – tell the user which other section is incomplete */
    const getMissingBackendField = (next: Snapshot): string | null => {
        if (!next.firstName.trim()) return "firstName";
        if (!next.lastName.trim()) return "lastName";
        if (!isFighter) return null;
        if (!next.dateOfBirth) return "dateOfBirth";
        if (next.trainingDisciplines.length === 0) return "disciplines";
        if (next.weightClasses.length === 0) return "weightClasses";
        if (!next.height) return "height";
        if (!next.weight) return "weight";
        if (!next.reach) return "reach";
        if (!next.stance) return "stance";
        if (!next.fightStyleDescription.trim()) return "fightStyle";
        if (next.fights.length === 0) return "fightHistory";
        return null;
    };

    const syncLocalStorage = (next: Snapshot, newImage?: string) => {
        try {
            const raw = localStorage.getItem("loginResponse");
            if (!raw) return;

            const loginResponse = JSON.parse(raw);
            loginResponse.firstName = next.firstName;
            loginResponse.lastName = next.lastName;
            loginResponse.bio = next.bio;
            loginResponse.country = next.residenceCountry;
            if (next.nickname) loginResponse.nickname = next.nickname;
            if (next.organizationName) loginResponse.organizationName = next.organizationName;
            if (newImage) loginResponse.profilePicture = newImage;

            localStorage.setItem("loginResponse", JSON.stringify(loginResponse));
        } catch (err) {
            console.error("Failed to sync profile to localStorage", err);
        }
    };

    const persist = async (patch: Partial<Snapshot>, newImage?: string): Promise<boolean> => {
        if (isBusyRef.current) return false;

        const next: Snapshot = { ...savedRef.current, ...patch };

        const missingField = getMissingBackendField(next);
        if (missingField) {
            toast.error(t("page.completeFirst", { field: t(`page.${missingField}`) }));
            return false;
        }

        isBusyRef.current = true;
        setIsPersisting(true);

        try {
            await updateUser(buildDto(next, member, newImage));

            setSaved(next);
            if (newImage) setImageUrl(newImage);
            syncLocalStorage(next, newImage);
            toast.success(t("profile_updated_successfully"));
            await onSubmitSuccess();
            return true;
        } catch (err) {
            toast.error(getErrorMessage(err, t("page.saveFailed")));
            return false;
        } finally {
            isBusyRef.current = false;
            setIsPersisting(false);
        }
    };

    /** Section → element id, used to scroll to the first section that has an error */
    const SECTION_ELEMENT_IDS: Record<SectionKey, string> = {
        bio: "section-profile",
        personal: "section-personal-details",
        location: "section-location-details",
        fighterBasic: "section-fighter-basic",
        fighterAdditional: "section-fighter-additional",
    };

    // fighter-only sections are not rendered for managers / organizations
    const visibleSections = SECTION_KEYS.filter(
        (section) => isFighter || (section !== "fighterBasic" && section !== "fighterAdditional")
    );
    const hasChanges = visibleSections.some((section) => isDirty(section));

    /** Top bar "Save changes": validates and saves every edited section in one request */
    const saveAll = async () => {
        if (isBusyRef.current) return;

        const dirtySections = visibleSections.filter((section) => isDirty(section));
        if (dirtySections.length === 0) return;

        const allErrors: FormErrors = {};
        dirtySections.forEach((section) => Object.assign(allErrors, validateSection(section)));

        setErrors((previous) => {
            const next = { ...previous };
            dirtySections.forEach((section) => SECTION_FIELDS[section].forEach((key) => delete next[key]));
            return { ...next, ...allErrors };
        });

        if (Object.keys(allErrors).length > 0) {
            toast.error(t("page.errors.fixHighlighted"));
            const firstBadSection = dirtySections.find((section) =>
                SECTION_FIELDS[section].some((key) => allErrors[key])
            );
            if (firstBadSection) {
                document
                    .getElementById(SECTION_ELEMENT_IDS[firstBadSection])
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            return;
        }

        // text values are trimmed before they are saved
        const cleanForm: ProfileForm = {
            ...form,
            bio: form.bio.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            nickname: form.nickname.trim(),
            organizationName: form.organizationName.trim(),
            team: form.team.trim(),
            gym: form.gym.trim(),
            fightStyleDescription: form.fightStyleDescription.trim(),
        };

        const patch: Partial<ProfileForm> = {};
        dirtySections.forEach((section) => SECTION_FIELDS[section].forEach((key) => copyField(patch, cleanForm, key)));

        setIsSavingAll(true);
        const isSaved = await persist(patch);
        setIsSavingAll(false);

        if (isSaved) setForm((previous) => ({ ...previous, ...patch }));
    };

    /** Top bar "Cancel": throw away every unsaved edit */
    const cancelAll = () => {
        setForm(toForm(savedRef.current));
        setErrors({});
    };

    // ── list commits (Add / Remove / Update) ──
    const commitSocialLinks = (next: SocialLink[]) => persist({ socialLinks: next });
    const commitAchievements = (next: string[]) => persist({ achievements: next });
    const commitFights = (next: FightItem[]) => persist({ fights: next });
    const commitYtLinks = (next: YoutubeItem[]) => persist({ ytLinks: next });
    const commitSponsors = (next: SponsorItem[]) => persist({ sponsors: next });

    // ── avatar ──
    const onPickImage = (file: File) => {
        if (!file.type.startsWith("image/")) {
            toast.error(t("page.errors.imageType"));
            return;
        }
        if (file.size > MAX_AVATAR_BYTES) {
            toast.error(t("page.errors.imageSize", { max: 10 }));
            return;
        }
        setCropSrc(URL.createObjectURL(file));
    };

    const closeCrop = () => {
        if (cropSrc) URL.revokeObjectURL(cropSrc);
        setCropSrc(null);
    };

    const onCropSave = async (croppedFile: File) => {
        closeCrop();
        setUploadingImage(true);
        try {
            const uploadedUrl = await uploadMediaFile(croppedFile);
            await persist({}, uploadedUrl);
        } catch (err) {
            toast.error(getErrorMessage(err, t("page.errors.imageUpload")));
        } finally {
            setUploadingImage(false);
        }
    };

    // ── delete account ──
    const showDeletionError = (err: unknown) => {
        if (isMissingEndpoint(err)) {
            toast.error(t("page.deleteUnavailable"));
            return;
        }
        toast.error(getErrorMessage(err, t("page.saveFailed")));
    };

    const openDeleteConfirm = async () => {
        setConfirmDeleteOpen(true);
    };

    const cancelDeletion = async () => {
        setDeletionBusy(true);
        try {
            await cancelAccountDeletion();
            setDeletionAt(null);
            toast.success(t("page.deleteCancelled"));
            await onSubmitSuccess();
        } catch (err) {
            showDeletionError(err);
        } finally {
            setDeletionBusy(false);
        }
    };

    const confirmDelete = async () => {
        setDeletionBusy(true);
        try {
            const response = await requestAccountDeletion();
            setDeletionAt(response.deletionScheduledAt ?? null);
            toast.success(t("page.deleteScheduled"));
            await onSubmitSuccess();
        } catch (err) {
            showDeletionError(err);
        } finally {
            setDeletionBusy(false);
            setConfirmDeleteOpen(false);
        }
    };

    // the page's top bar always calls the latest save / cancel through this ref
    const latestActionsRef = useRef({ saveAll, cancelAll });
    latestActionsRef.current = { saveAll, cancelAll };

    useEffect(() => {
        onActionsChange?.({
            hasChanges,
            saving: isSavingAll || isPersisting,
            save: () => void latestActionsRef.current.saveAll(),
            cancel: () => latestActionsRef.current.cancelAll(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasChanges, isSavingAll, isPersisting]);

    // remove the buttons again when this page is left
    useEffect(() => {
        return () => onActionsChange?.(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uiProps: UpdateProfileUIProps = {
        member,
        isFighter,
        isManager,
        isOrganization,
        form,
        setField,
        errors,
        clearError,
        isDirty,
        countries,
        disciplineOptions,
        weightClassOptions,
        isPersisting,
        socialLinks: saved.socialLinks,
        commitSocialLinks,
        achievements: saved.achievements,
        commitAchievements,
        fights: saved.fights,
        commitFights,
        ytLinks: saved.ytLinks,
        commitYtLinks,
        sponsors: saved.sponsors,
        commitSponsors,
        imageUrl,
        uploadingImage,
        onPickImage,
        deletion: {
            scheduledAt: deletionAt,
            busy: deletionBusy,
            request: openDeleteConfirm,
            cancel: cancelDeletion,
        },
    };

    return (
        <>
            <ProfileSettingsTab {...uiProps} />

            {cropSrc && <ImageCropModal imageSrc={cropSrc} onSave={onCropSave} onCancel={closeCrop} />}

            <ConfirmModal
                isOpen={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                cardTitle={t("page.deleteAccount")}
                cardDescription={t("page.deleteConfirmBody")}
                buttonText={t("page.deleteAccount")}
                onConfirm={confirmDelete}
            />
        </>
    );
};
